import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { PrintoutOrientation } from "../../../utils/models";
import { SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { SettingRow } from "../../UIToolkit/molecules/SettingRow";
import { DIMMED_OPACITY, MAX_FIT_PAGES, NUMERIC_INPUT_WIDTH, SCHEDULE_EXPORT_TEST_ID } from "./consts";
import type { MonthlyExportSettingsValue } from "./types";

export type MonthlyPageSetupSectionProps = {
	value: Pick<MonthlyExportSettingsValue, "orientation" | "fitToPage" | "fitToWidth" | "fitToHeight">;
	onChange: <K extends keyof MonthlyExportSettingsValue>(key: K, next: MonthlyExportSettingsValue[K]) => void;
	disabled?: boolean;
};

/** How the sheet lands on paper: orientation, and whether/how it scales to fit. Pure presentation. */
export const MonthlyPageSetupSection: FC<MonthlyPageSetupSectionProps> = ({ value, onChange, disabled }) => {
	const { t } = useTranslation();

	return (
		<>
			{/* A month of date columns needs the wide edge of the page (AC #1). */}
			<SystemFlatSwitch
				data-testid={`${SCHEDULE_EXPORT_TEST_ID}-orientation`}
				options={[
					// Per-option, because that is the only `disabled` the switch has — the section's own flag has
					// to reach every option or the buttons stay clickable while the rest of the panel is locked.
					{ label: t("printout.orientation.landscape"), value: PrintoutOrientation.Landscape, disabled },
					{ label: t("printout.orientation.portrait"), value: PrintoutOrientation.Portrait, disabled },
				]}
				activeIndexOrValue={value.orientation}
				onSwitch={(option) => onChange("orientation", option.value)}
				textWrap="nowrap"
			/>
			<SystemRoundedCheckbox
				data-testid={`${SCHEDULE_EXPORT_TEST_ID}-fit-to-page`}
				checked={value.fitToPage}
				onChange={(checked) => onChange("fitToPage", checked)}
				label={t("printout.fit.to.page")}
				disabled={disabled}
			/>
			{/* Indented under the switch they qualify, and dimmed when it is off — as in the Daily Printout
			    panel, which is where head nurses already know these two boxes from. */}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
				<SettingRow
					label={t("printout.fit.to.width")}
					sx={{ opacity: value.fitToPage ? 1 : DIMMED_OPACITY }}
					control={
						<SystemValidationTextField
							value={String(value.fitToWidth)}
							disabled={disabled || !value.fitToPage}
							// Clamped to the ceiling as well as the floor: the backend bounds this field at
							// `MAX_FIT_PAGES`, so letting a larger number through would 422 both the preview
							// request and the settings PUT.
							onChange={(event) => onChange("fitToWidth", Math.min(MAX_FIT_PAGES, Math.max(1, parseInt(event.target.value, 10) || 1)))}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: NUMERIC_INPUT_WIDTH }}
							dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-fit-to-width`}
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 1 && Number(v) <= MAX_FIT_PAGES,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
					}
				/>
				<SettingRow
					label={t("printout.fit.to.height")}
					sx={{ opacity: value.fitToPage ? 1 : DIMMED_OPACITY }}
					control={
						<SystemValidationTextField
							value={String(value.fitToHeight)}
							disabled={disabled || !value.fitToPage}
							// 0 is meaningful here and 1 is not a floor: it means "as many pages down as it takes".
							// Same backend ceiling as the width above.
							onChange={(event) => onChange("fitToHeight", Math.min(MAX_FIT_PAGES, Math.max(0, parseInt(event.target.value, 10) || 0)))}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: NUMERIC_INPUT_WIDTH }}
							dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-fit-to-height`}
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= MAX_FIT_PAGES,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
					}
				/>
			</Box>
		</>
	);
};
